import useGlobalContext from "hooks/globalData";
import { useQuery } from "react-query";
import {
  AccreditationChainRequest,
  GetQualificationsRequest,
  IAccreditationChainItem,
  QualificationFilter,
} from "services/KnowfullyAdminClient";
import { NO_LINK_VALUE, type ReferenceRow } from "./qualificationLinkState";
import { withAcronym } from "./wizardSave";

// A row is only selectable when it carries both ids the save writes, so a chain item
// missing either one is dropped rather than shown as an unsavable option.
const toReferenceRow = (item: IAccreditationChainItem): ReferenceRow | null =>
  item.licensureBodyId && item.accreditationBodyId
    ? {
        lb: withAcronym(item.licensureBodyName, item.licensureBodyAcronym),
        ab: withAcronym(
          item.accreditationBodyName,
          item.accreditationBodyAcronym,
        ),
        sa: item.submissionAuthorityName ?? NO_LINK_VALUE,
        licensureBodyId: item.licensureBodyId,
        accreditationBodyId: item.accreditationBodyId,
      }
    : null;

// The chain endpoint resolves per Accreditation Body, so the full set of valid
// LB -> AB -> SA combinations is the union over the organization's qualifications.
export const useAccreditationChainRows = (
  organizationId: string,
  enabled: boolean,
) => {
  const { qualificationService } = useGlobalContext();

  const {
    data: qualifications,
    isFetching: isFetchingQualifications,
  } = useQuery(
    ["qualificationService.getQualifications", organizationId],
    () =>
      qualificationService.getQualifications(
        new GetQualificationsRequest({
          organizationId,
          sortField: "value",
          parameters: QualificationFilter.fromJS({}),
        }),
      ),
    { enabled: enabled && !!organizationId },
  );

  const qualificationIds = (qualifications?.data ?? [])
    .map((qualification) => qualification.id)
    .filter((id): id is string => !!id);

  const { data: chains, isFetching: isFetchingChains } = useQuery(
    ["qualificationService.getAccreditationChains", qualificationIds],
    () =>
      qualificationService.getAccreditationChains(
        new AccreditationChainRequest({ qualificationIds }),
      ),
    { enabled: qualificationIds.length > 0 },
  );

  const rows = (chains?.data ?? [])
    .map(toReferenceRow)
    .filter((row): row is ReferenceRow => row !== null);

  return {
    rows,
    isLoading: isFetchingQualifications || isFetchingChains,
  };
};
